import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import eventEmitter from "../utils/eventEmitter";

/** How long "Back online" stays up after the connection returns. */
export const BACK_ONLINE_MS = 3000;

const browserOnline = () => navigator.onLine !== false;

/**
 * `navigator.onLine` is the only offline signal the widget has: socket-client.js emits
 * `socket_reconnected` on a re-open but nothing on the way down, so "connection lost" cannot be told
 * truthfully in v1 (chat-experience 11 §6 gap 3) — the reconnect signal only clears a stale offline
 * state and flashes "Back online".
 *
 * @returns {{ online: boolean, justReturned: boolean }} `justReturned` is true for BACK_ONLINE_MS
 *   after an offline → online flip, then clears itself.
 */
export function useOnlineStatus() {
	const [online, setOnline] = useState(browserOnline);
	const [justReturned, setJustReturned] = useState(false);
	const onlineRef = useRef(online);
	const timerRef = useRef(null);

	useEffect(() => {
		const goOnline = () => {
			if (onlineRef.current) return;
			onlineRef.current = true;
			setOnline(true);
			setJustReturned(true);
			clearTimeout(timerRef.current);
			timerRef.current = setTimeout(() => setJustReturned(false), BACK_ONLINE_MS);
		};
		const goOffline = () => {
			onlineRef.current = false;
			clearTimeout(timerRef.current);
			setOnline(false);
			setJustReturned(false);
		};
		window.addEventListener("online", goOnline);
		window.addEventListener("offline", goOffline);
		eventEmitter.on("socket_reconnected", goOnline);
		return () => {
			window.removeEventListener("online", goOnline);
			window.removeEventListener("offline", goOffline);
			eventEmitter.off("socket_reconnected", goOnline);
			clearTimeout(timerRef.current);
		};
	}, []);

	return { online, justReturned };
}

/** The banner at the top of the chat column. Nothing while online, unless the connection just came back. */
export default function OfflineBanner({ online, justReturned, msg }) {
	if (online && !justReturned) return null;
	return (
		<Box className={`widget-offline-banner${online ? " is-back" : ""}`} role="status">
			{online ? msg("chat.back_online", "Back online") : msg("chat.offline", "You are offline")}
		</Box>
	);
}
